import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Phone, Mail, MessageCircle, Send, MapPin, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Overline } from "../Reveal";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { BRAND } from "../../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BUDGETS = ["Under ₹50K", "₹50K – ₹1L", "₹1L – ₹3L", "₹3L – ₹5L", "₹5L+"];

const inputClass =
  "w-full bg-transparent border-b border-white/15 py-2.5 font-inter text-sm text-white placeholder:text-white/30 focus:border-gk-gold transition-colors duration-400";

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", business: "", budget: "", details: "" });
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.details) {
      toast.error("Please fill in your name, phone, email and project details.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you. Your enquiry has been received — we'll be in touch shortly.");
      setForm({ name: "", phone: "", email: "", business: "", budget: "", details: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail?.[0]?.msg || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check your details and try again.");
    } finally {
      setLoading(false);
    }
  };

  const waLink = `https://wa.me/${BRAND.phoneRaw}?text=${encodeURIComponent("Hi GK Brandify, I'd like to start a project.")}`;
  const qr = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&bgcolor=0A0A0A&color=D4AF37&qzone=1&data=${encodeURIComponent(waLink)}`;
  const mapEmbed = `https://www.google.com/maps?q=${encodeURIComponent(BRAND.address)}&output=embed`;

  return (
    <section id="contact" data-testid="contact-section" className="relative py-16 md:py-24 overflow-hidden">
      <div className="ambient-emerald" style={{ width: 560, height: 560, bottom: "-15%", right: "-12%", opacity: 0.3 }} />

      <div className="relative z-10 mx-auto max-w-6xl px-6 md:px-10 grid lg:grid-cols-12 gap-10">
        {/* Left */}
        <div className="lg:col-span-5">
          <Reveal>
            <Overline>Let's Create</Overline>
            <h2 className="font-cinzel text-3xl sm:text-4xl font-semibold text-white mt-4 leading-[0.98]">
              Let's <span className="text-gold-metallic">Talk.</span>
            </h2>
            <p className="font-poppins text-sm text-white/55 mt-4 max-w-md leading-relaxed">
              Tell us about your vision. We reply to every serious enquiry within one business day.
            </p>
          </Reveal>

          <Reveal delay={0.1} className="mt-8 space-y-2.5">
            <a data-testid="contact-whatsapp" href={waLink} target="_blank" rel="noreferrer" className="group flex items-center gap-4 glass rounded-2xl p-4 hover:border-gk-gold/40 transition-colors">
              <span className="w-11 h-11 rounded-xl bg-gk-emerald2/30 flex items-center justify-center"><MessageCircle size={20} className="text-gk-champagne" /></span>
              <div><p className="font-inter text-xs uppercase tracking-widest text-white/40">WhatsApp</p><p className="font-poppins text-white group-hover:text-gk-champagne transition-colors">{BRAND.phone}</p></div>
            </a>
            <a data-testid="contact-call" href={`tel:+${BRAND.phoneRaw}`} className="group flex items-center gap-4 glass rounded-2xl p-4 hover:border-gk-gold/40 transition-colors">
              <span className="w-11 h-11 rounded-xl bg-gk-emerald2/30 flex items-center justify-center"><Phone size={20} className="text-gk-champagne" /></span>
              <div><p className="font-inter text-xs uppercase tracking-widest text-white/40">Call</p><p className="font-poppins text-white group-hover:text-gk-champagne transition-colors">{BRAND.phone}</p></div>
            </a>
            <a data-testid="contact-email" href={`mailto:${BRAND.email}`} className="group flex items-center gap-4 glass rounded-2xl p-4 hover:border-gk-gold/40 transition-colors">
              <span className="w-11 h-11 rounded-xl bg-gk-emerald2/30 flex items-center justify-center"><Mail size={20} className="text-gk-champagne" /></span>
              <div><p className="font-inter text-xs uppercase tracking-widest text-white/40">Email</p><p className="font-poppins text-white group-hover:text-gk-champagne transition-colors">{BRAND.email}</p></div>
            </a>
          </Reveal>

          <Reveal delay={0.15} className="mt-6 flex gap-4 items-center">
            <img src={qr} alt="WhatsApp QR" className="w-20 h-20 rounded-xl glass p-1.5" />
            <div className="flex items-start gap-2">
              <MapPin size={18} className="text-gk-gold mt-0.5 shrink-0" />
              <p className="font-poppins text-sm text-white/50 max-w-[180px]">{BRAND.address}</p>
            </div>
          </Reveal>

          <Reveal delay={0.2} className="mt-6 flex flex-wrap gap-2">
            {BRAND.socials.map((s) => (
              <a
                key={s.label}
                data-testid={`contact-social-${s.label.toLowerCase()}`}
                href={s.href}
                target="_blank"
                rel="noreferrer"
                className="glass rounded-full px-3.5 py-1.5 font-inter text-xs text-white/60 hover:text-gk-champagne transition-colors"
              >
                {s.label}
              </a>
            ))}
          </Reveal>
        </div>

        {/* Right — form */}
        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <form data-testid="contact-form" onSubmit={submit} className="glass-strong rounded-3xl p-6 md:p-8">
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="font-inter text-xs uppercase tracking-widest text-white/40">Name *</label>
                  <input data-testid="contact-input-name" className={inputClass} placeholder="Your full name" value={form.name} onChange={(e) => update("name", e.target.value)} />
                </div>
                <div>
                  <label className="font-inter text-xs uppercase tracking-widest text-white/40">Phone *</label>
                  <input data-testid="contact-input-phone" className={inputClass} placeholder="+91 00000 00000" value={form.phone} onChange={(e) => update("phone", e.target.value)} />
                </div>
                <div>
                  <label className="font-inter text-xs uppercase tracking-widest text-white/40">Email *</label>
                  <input data-testid="contact-input-email" type="email" className={inputClass} placeholder="you@company.com" value={form.email} onChange={(e) => update("email", e.target.value)} />
                </div>
                <div>
                  <label className="font-inter text-xs uppercase tracking-widest text-white/40">Business</label>
                  <input data-testid="contact-input-business" className={inputClass} placeholder="Company name" value={form.business} onChange={(e) => update("business", e.target.value)} />
                </div>
                <div className="md:col-span-2">
                  <label className="font-inter text-xs uppercase tracking-widest text-white/40 block mb-2">Budget</label>
                  <Select value={form.budget} onValueChange={(v) => update("budget", v)}>
                    <SelectTrigger data-testid="contact-select-budget" className="w-full bg-transparent border-white/15 h-12 rounded-lg font-inter text-white focus:ring-gk-gold data-[placeholder]:text-white/30">
                      <SelectValue placeholder="Select a range" />
                    </SelectTrigger>
                    <SelectContent className="bg-gk-panel border-white/10 text-white">
                      {BUDGETS.map((b) => (
                        <SelectItem key={b} value={b} className="focus:bg-gk-emerald/40 focus:text-gk-champagne">{b}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <label className="font-inter text-xs uppercase tracking-widest text-white/40">Project Details *</label>
                  <textarea data-testid="contact-input-details" rows={4} className={`${inputClass} resize-none`} placeholder="Tell us about your project, goals and timeline…" value={form.details} onChange={(e) => update("details", e.target.value)} />
                </div>
              </div>

              <button
                data-testid="contact-submit"
                type="submit"
                disabled={loading}
                className="btn-gold w-full mt-7 py-3 rounded-full font-inter text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-70"
              >
                {loading ? <><Loader2 size={18} className="animate-spin" /> Sending…</> : <>Start Your Project <Send size={17} /></>}
              </button>
            </form>
          </Reveal>

          <Reveal delay={0.15} className="mt-6 rounded-3xl overflow-hidden ring-1 ring-white/10 h-56">
            <iframe
              title="GK Brandify Location"
              src={mapEmbed}
              className="w-full h-full grayscale contrast-125 opacity-80"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </Reveal>
        </div>
      </div>
    </section>
  );
}
